import { Card } from '@/components/ui/card';
import { format } from 'date-fns';

interface EstimatePDFPreviewProps {
  estimateData: any;
}

const materialOptions = [
  { value: 'asphalt', label: 'Asphalt Shingles', costPerSq: 350 },
  { value: 'metal', label: 'Metal Roofing', costPerSq: 850 },
  { value: 'tile', label: 'Tile', costPerSq: 1200 },
  { value: 'flat', label: 'Flat/TPO', costPerSq: 600 },
];

const complexityOptions = [
  { value: 'simple', label: 'Simple', multiplier: 1.0 },
  { value: 'moderate', label: 'Moderate', multiplier: 1.2 },
  { value: 'complex', label: 'Complex', multiplier: 1.4 },
];

export function EstimatePDFPreview({ estimateData }: EstimatePDFPreviewProps) {
  const roofSquares = estimateData.roofSquares || 20;
  const selectedMaterial = materialOptions.find(m => m.value === estimateData.material) || materialOptions[0];
  const selectedComplexity = complexityOptions.find(c => c.value === estimateData.complexity) || complexityOptions[0];

  const basePrice = roofSquares * selectedMaterial.costPerSq;
  const total = Math.round(basePrice * selectedComplexity.multiplier);

  return (
    <Card className="p-6 bg-white text-black">
      {/* Header */}
      <div className="border-b pb-4 mb-4">
        <h1 className="text-2xl font-bold">Quick Estimate</h1>
        <p className="text-sm text-gray-600 mt-1">
          Generated on {format(new Date(), 'MMMM d, yyyy')}
        </p>
      </div>

      {/* Project Details */}
      <div className="mb-6">
        <h2 className="font-semibold text-lg mb-3">Project Details</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Roof Size:</span>
            <span className="font-medium">{roofSquares} squares</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Material:</span>
            <span className="font-medium">{selectedMaterial.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Complexity:</span>
            <span className="font-medium">{selectedComplexity.label}</span>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="mb-6">
        <h2 className="font-semibold text-lg mb-3">Cost Breakdown</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Base Cost:</span>
            <span className="font-medium">${basePrice.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Complexity Factor:</span>
            <span className="font-medium">{selectedComplexity.multiplier}x</span>
          </div>
        </div>
      </div>

      {/* Total */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold">Estimated Total:</span>
          <span className="text-3xl font-bold text-blue-600">
            ${total.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> This is a preliminary estimate for budgeting purposes only.
          Final pricing may vary based on site inspection, material availability, and project specifics.
          This estimate is valid for 30 days from the date above.
        </p>
      </div>
    </Card>
  );
}
